import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronRight, Play, Loader2, CheckCircle2, AlertTriangle,
  ShieldAlert, Type, MousePointerClick, Sparkles, RotateCcw, Lightbulb,
} from 'lucide-react';
import { PlaceholderPickerBar } from './PlaceholderPickerBar';

const API = '/api';

const SAFETY_COLORS = {
  1: 'text-emerald-600 dark:text-emerald-400',
  2: 'text-sky-600 dark:text-sky-400',
  3: 'text-amber-600 dark:text-amber-400',
  4: 'text-red-600 dark:text-red-400',
};

const SAFETY_LABELS = { 1: 'Auto', 2: 'Affirm', 3: 'Button', 4: 'Typed' };

function buildSampleInput(inputSchema) {
  const props = inputSchema?.properties || {};
  const required = new Set(inputSchema?.required || []);
  const sample = {};
  Object.entries(props).forEach(([key, def]) => {
    if (!required.has(key)) return;
    const t = def?.type;
    if (t === 'string') sample[key] = '';
    else if (t === 'integer' || t === 'number') sample[key] = 0;
    else if (t === 'boolean') sample[key] = false;
    else if (t === 'array') sample[key] = [];
    else if (t === 'object') sample[key] = {};
    else sample[key] = '';
  });
  return sample;
}

function diffSummary(diff) {
  if (!diff) return null;
  const parts = [];
  if (diff.added && Object.keys(diff.added).length > 0) parts.push(`${Object.keys(diff.added).length} added`);
  if (diff.removed && Object.keys(diff.removed).length > 0) parts.push(`${Object.keys(diff.removed).length} removed`);
  if (diff.changed && Object.keys(diff.changed).length > 0) parts.push(`${Object.keys(diff.changed).length} changed`);
  return parts.join(', ') || 'no changes';
}

const ResultBlock = ({ result, onUndo, undoing }) => {
  if (!result) return null;
  if (result.success) {
    const summary = diffSummary(result.diff);
    return (
      <div
        className="mt-3 p-3 rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40"
        data-testid="try-result-success"
      >
        <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 text-xs font-medium">
          <CheckCircle2 className="w-4 h-4" />
          Executed successfully
          {result.action_run_id && (
            <span className="font-mono text-[10px] text-emerald-600 dark:text-emerald-400">
              · {result.action_run_id}
            </span>
          )}
          {summary && (
            <Badge variant="outline" className="ml-auto text-[10px] border-emerald-300 text-emerald-700 dark:border-emerald-800 dark:text-emerald-300">
              {summary}
            </Badge>
          )}
        </div>
        {result.output != null && (
          <pre className="mt-2 text-[10px] bg-white/60 dark:bg-black/30 rounded p-2 max-h-40 overflow-auto whitespace-pre-wrap break-words">
            {typeof result.output === 'string' ? result.output : JSON.stringify(result.output, null, 2)}
          </pre>
        )}
        {result.undo_deadline && (
          <Button
            size="sm"
            variant="outline"
            className="mt-2 h-7 gap-1 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-900 dark:text-amber-400 dark:hover:bg-amber-950/40"
            onClick={onUndo}
            disabled={undoing}
            data-testid="try-undo-btn"
          >
            {undoing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
            Undo this action
          </Button>
        )}
      </div>
    );
  }

  if (result.pending_action_id) {
    // Confirmation required — render is in the parent so we can collect input.
    return null;
  }

  const err = result.error || {};
  return (
    <div
      className="mt-3 p-3 rounded-lg border border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40"
      data-testid="try-result-error"
    >
      <div className="flex items-center gap-2 text-red-700 dark:text-red-300 text-xs font-medium">
        <AlertTriangle className="w-4 h-4" />
        <span className="font-mono text-[10px] uppercase">{err.error_code || 'AI-TOOL'}</span>
        <span>{err.title || 'Failed'}</span>
      </div>
      {err.message && (
        <p className="mt-1 text-[11px] text-red-800 dark:text-red-200">{err.message}</p>
      )}
      {err.recovery_hint && (
        <p className="mt-1 text-[10px] text-red-600 dark:text-red-300 italic">{err.recovery_hint}</p>
      )}
    </div>
  );
};

const ConfirmationBlock = ({ result, onConfirm, confirming }) => {
  const [token] = useState(() => result?.error?.details?.confirmation_token || null);
  const [phrase, setPhrase] = useState('');
  const safetyLevel = result?.safety_level;
  const policy = result?.confirmation_policy || 'button';
  const requiredPhrase = result?.error?.details?.required_typed_phrase;

  if (!result?.pending_action_id) return null;

  if (policy === 'conversational') {
    return (
      <div
        className="mt-3 p-3 rounded-lg border border-sky-300 bg-sky-50 dark:border-sky-900 dark:bg-sky-950/40"
        data-testid="try-confirm-conversational"
      >
        <div className="flex items-center gap-2 text-sky-700 dark:text-sky-300 text-xs font-medium">
          <Sparkles className="w-4 h-4" />
          Level 2 · Affirmation required
        </div>
        <p className="mt-1 text-[11px] text-sky-800 dark:text-sky-200">
          Confirm to proceed (in chat, a "yes / go ahead" reply is enough).
        </p>
        <Button
          size="sm"
          className="mt-2 h-7 text-[11px] bg-sky-600 hover:bg-sky-700"
          onClick={() => onConfirm({ confirmation_token: null, typed_phrase: null })}
          disabled={confirming}
          data-testid="try-confirm-btn"
        >
          {confirming ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
          Yes, run it
        </Button>
      </div>
    );
  }

  if (policy === 'button') {
    return (
      <div
        className="mt-3 p-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40"
        data-testid="try-confirm-button"
      >
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-xs font-medium">
          <MousePointerClick className="w-4 h-4" />
          Level 3 · Button confirmation required
        </div>
        <p className="mt-1 text-[11px] text-amber-800 dark:text-amber-200">
          {result.error?.message || 'Click confirm to execute.'}
        </p>
        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700"
            onClick={() => onConfirm({ confirmation_token: token, typed_phrase: null })}
            disabled={confirming || !token}
            data-testid="try-confirm-btn"
          >
            {confirming ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
            Confirm
          </Button>
        </div>
      </div>
    );
  }

  // Level 4 — typed phrase
  return (
    <div
      className="mt-3 p-3 rounded-lg border border-red-400 bg-red-50 dark:border-red-900 dark:bg-red-950/40"
      data-testid="try-confirm-typed"
    >
      <div className="flex items-center gap-2 text-red-700 dark:text-red-300 text-xs font-medium">
        <ShieldAlert className="w-4 h-4" />
        Level 4 · Typed confirmation required
      </div>
      <p className="mt-1 text-[11px] text-red-800 dark:text-red-200">
        This action is destructive. Type the phrase below to confirm.
      </p>
      {requiredPhrase && (
        <p className="mt-2 text-[11px] font-mono bg-white/70 dark:bg-black/30 inline-block px-2 py-1 rounded border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300">
          {requiredPhrase}
        </p>
      )}
      <div className="flex gap-2 mt-2">
        <Input
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          placeholder="Type the exact phrase here"
          className="h-7 text-[11px] flex-1"
          data-testid="try-typed-phrase-input"
        />
        <Button
          size="sm"
          className="h-7 text-[11px] bg-red-600 hover:bg-red-700"
          onClick={() => onConfirm({ confirmation_token: null, typed_phrase: phrase })}
          disabled={confirming || phrase !== requiredPhrase}
          data-testid="try-confirm-btn"
        >
          {confirming ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
          Confirm Delete
        </Button>
      </div>
    </div>
  );
};

export const CapabilityTryRow = ({ cap }) => {
  const [expanded, setExpanded] = useState(false);
  const [args, setArgs] = useState(() =>
    JSON.stringify(buildSampleInput(cap.input_schema || {}), null, 2)
  );
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [result, setResult] = useState(null);

  const slColor = SAFETY_COLORS[cap.safety_level] || 'text-muted-foreground';

  const run = async () => {
    let parsed = {};
    try {
      parsed = args.trim() ? JSON.parse(args) : {};
    } catch (e) {
      toast.error('Invalid JSON in arguments');
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/ai/v2/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ capability_id: cap.capability_id, input_data: parsed }),
      });
      const data = await res.json();
      setResult(data);
    } catch (e) {
      toast.error('Request failed', { description: String(e) });
    } finally {
      setRunning(false);
    }
  };

  const confirm = async ({ confirmation_token, typed_phrase }) => {
    if (!result?.pending_action_id) return;
    setConfirming(true);
    try {
      const res = await fetch(`${API}/ai/v2/pending-actions/${result.pending_action_id}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirmation_token, typed_phrase }),
      });
      const data = await res.json();
      setResult(data);
      if (data.success) {
        toast.success(`${cap.name} confirmed`, {
          description: data.action_run_id ? `action ${data.action_run_id}` : undefined,
        });
      }
    } catch (e) {
      toast.error('Confirm failed', { description: String(e) });
    } finally {
      setConfirming(false);
    }
  };

  const undo = async () => {
    if (!result?.action_run_id) return;
    setUndoing(true);
    try {
      const res = await fetch(`${API}/ai/v2/actions/${result.action_run_id}/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: '{}',
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Action undone', { description: data.message });
        setResult({ ...result, undo_deadline: null, _undone: true });
      } else {
        const detail = data.detail || data;
        toast.error(detail.error_code || 'AI-UNDO', { description: detail.message });
      }
    } catch (e) {
      toast.error('Undo failed', { description: String(e) });
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div data-testid={`v2-cap-row-${cap.capability_id}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/30 text-left"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {expanded ? <ChevronDown className="w-3 h-3 flex-shrink-0 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 flex-shrink-0 text-muted-foreground" />}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium truncate">{cap.name}</p>
            <p className="text-[10px] font-mono text-muted-foreground truncate">{cap.capability_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 ml-3 flex-shrink-0">
          <span
            className={cn('text-[10px] font-mono font-semibold', slColor)}
            title={`Safety Level ${cap.safety_level} — ${SAFETY_LABELS[cap.safety_level] || ''}`}
          >
            L{cap.safety_level}
          </span>
          {cap.external_writes && (
            <Badge variant="outline" className="text-[9px] py-0 px-1 border-purple-300 text-purple-600 dark:text-purple-400 dark:border-purple-900">ext</Badge>
          )}
          {cap.undo_supported && (
            <Badge variant="outline" className="text-[9px] py-0 px-1 border-emerald-300 text-emerald-600 dark:text-emerald-400 dark:border-emerald-900">undo</Badge>
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 bg-muted/10 border-t" data-testid={`v2-cap-tryarea-${cap.capability_id}`}>
          {cap.description && (
            <p className="text-[11px] text-muted-foreground mb-2">{cap.description}</p>
          )}
          {cap.examples && cap.examples.length > 0 && (
            <div className="mb-2" data-testid={`v2-cap-examples-${cap.capability_id}`}>
              <label className="text-[10px] uppercase font-semibold text-muted-foreground flex items-center gap-1">
                <Lightbulb className="w-3 h-3" />
                Examples
              </label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {cap.examples.map((ex, idx) => (
                  <button
                    key={idx}
                    type="button"
                    title={ex.description}
                    onClick={() => setArgs(JSON.stringify(ex.args, null, 2))}
                    className="text-[11px] px-2 py-0.5 rounded-full border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-950/60 transition"
                    data-testid={`v2-cap-example-${cap.capability_id}-${idx}`}
                  >
                    {ex.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <label className="text-[10px] uppercase font-semibold text-muted-foreground">
            Input (JSON)
          </label>
          <PlaceholderPickerBar argsText={args} onArgsChange={setArgs} />
          <Textarea
            value={args}
            onChange={(e) => setArgs(e.target.value)}
            rows={Math.min(8, Math.max(3, args.split('\n').length))}
            className="mt-1 text-[11px] font-mono"
            data-testid={`v2-cap-args-${cap.capability_id}`}
          />
          <div className="flex items-center gap-2 mt-2">
            <Button
              size="sm"
              onClick={run}
              disabled={running}
              className="h-7 text-[11px] gap-1"
              data-testid={`v2-cap-try-btn-${cap.capability_id}`}
            >
              {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              Try it
            </Button>
            <span className="text-[10px] text-muted-foreground">
              POSTs to <span className="font-mono">/api/ai/v2/execute</span>
            </span>
          </div>
          <ConfirmationBlock result={result} onConfirm={confirm} confirming={confirming} />
          <ResultBlock result={result} onUndo={undo} undoing={undoing} />
        </div>
      )}
    </div>
  );
};

export default CapabilityTryRow;
